import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { AdminPartnerDocument, ApiError } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDate } from '@/lib/utils';
import { formatFileSize } from '@/pages/partner/Agreement';
import { Download, FileText, Loader2, Trash2, Upload } from 'lucide-react';

/** Point a pre-opened tab at the document (PDF) or trigger a named download (Word); release the object URL afterwards. */
function deliverDocument(tab: Window | null, url: string, fileName: string, mimeType: string): boolean {
  const isPdf = mimeType === 'application/pdf';
  if (isPdf && tab) {
    tab.location.href = url;
  } else {
    if (tab) tab.close();
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  const blocked = isPdf && !tab;   // a PDF that could not be shown because the tab was blocked: the caller keeps the URL for a fallback link
  // Otherwise navigation or the download has already started; release the URL once the browser has taken it
  if (!blocked) window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return blocked;
}

const ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const MAX_BYTES = 25 * 1024 * 1024;

/**
 * Admin card on the partner detail page: upload the partner agreement (and amendments) for one partner,
 * list what the partner can see, and remove documents. Files go straight from the browser to S3 via a
 * pre-signed URL; the portal only stores the record.
 */
export function PartnerDocumentsCard({ partnerId, partnerName }: { partnerId: string; partnerName: string }) {
  const [documents, setDocuments] = useState<AdminPartnerDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [fallback, setFallback] = useState<{ id: string; url: string } | null>(null);
  useEffect(() => () => { if (fallback) URL.revokeObjectURL(fallback.url); }, [fallback]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const data = await api.getPartnerDocuments(partnerId);
      setDocuments(data.documents);
    } catch (e) {
      setError((e as ApiError).error || 'Could not load documents');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const pick = (f: File | null) => {
    setError(null);
    setMessage(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setError('That file is larger than 25 MB.');
      setFile(null);
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const upload = async () => {
    if (!file) return;
    setIsSaving(true);
    setError(null);
    setMessage(null);
    setProgress(0);
    try {
      await api.uploadPartnerDocument(partnerId, file, title.trim() || file.name.replace(/\.[^.]+$/, ''), setProgress);
      setMessage(`Uploaded. ${partnerName} can now see it under Agreement.`);
      setFile(null);
      setTitle('');
      if (fileInput.current) fileInput.current.value = '';
      await load();
    } catch (e) {
      setError((e as ApiError).error || 'Upload failed');
    } finally {
      setIsSaving(false);
      setProgress(null);
    }
  };

  // Open the tab during the click (user activation), fetch the file with the session token, then point the tab at it
  const open = async (doc: AdminPartnerDocument) => {
    setBusyId(doc.id);
    setFallback(null);
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const { url, fileName, mimeType } = await api.fetchPartnerDocumentObjectUrl(partnerId, doc.id);
      if (deliverDocument(tab, url, fileName, mimeType)) setFallback({ id: doc.id, url });
    } catch (e) {
      if (tab) tab.close();
      setError((e as ApiError).error || 'Could not open the document');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (doc: AdminPartnerDocument) => {
    if (!window.confirm(`Remove "${doc.title}"? The partner will no longer see it.`)) return;
    setBusyId(doc.id);
    setError(null);
    try {
      await api.deletePartnerDocument(partnerId, doc.id);
      setMessage('Document removed.');
      await load();
    } catch (e) {
      setError((e as ApiError).error || 'Could not remove the document');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agreement and documents</CardTitle>
        <CardDescription>
          Upload the signed partner agreement and any amendments. The partner sees these under Agreement in their menu. PDF or Word, up
          to 25 MB. Files are kept on the portal server and included in its backups.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {message && <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{message}</div>}

        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div className="space-y-1">
            <Label htmlFor={`doc-file-${partnerId}`}>File</Label>
            <Input id={`doc-file-${partnerId}`} ref={fileInput} type="file" accept={ACCEPT} onChange={(e) => pick(e.target.files?.[0] || null)} disabled={isSaving} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`doc-title-${partnerId}`}>Title shown to the partner</Label>
            <Input id={`doc-title-${partnerId}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Partner Agreement 2026" maxLength={200} disabled={isSaving} />
          </div>
          <Button type="button" onClick={upload} disabled={!file || isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            {isSaving && progress !== null ? `Uploading ${progress}%` : 'Upload'}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-gray-600">No documents yet. The partner's Agreement page tells them nothing has been shared.</p>
        ) : (
          <ul className="divide-y">
            {documents.map((doc) => (
              <li key={doc.id} className="py-3 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <FileText className="h-5 w-5 text-abby-primary flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{doc.title}</p>
                    <p className="text-xs text-gray-500">
                      {doc.file_name} · {formatFileSize(doc.file_size)} · added {formatDate(doc.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {fallback?.id === doc.id && (
                    <a href={fallback.url} target="_blank" rel="noopener noreferrer" className="text-sm text-abby-primary underline">
                      Click to open
                    </a>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => open(doc)} disabled={busyId === doc.id}>
                    <Download className="h-4 w-4 mr-1" /> Open
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => remove(doc)} disabled={busyId === doc.id} className="text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4 mr-1" /> Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
